(ns app.front.view.group.admin.templates.stamps.canvas
  "Stamp previews and canvas interaction. Draft persistence, selection and toolbar
   actions belong to the caller; all geometry is expressed in PDF points."
  (:require [app.domain.stamp.model :as stamp-model]
            [app.front.stamp.render :as render]
            [app.front.util :as futil]
            [core.front.icons :as icons]
            [reagent.core :as r]))

(defn preview
  "Compose the table's fetched stamp snapshot once per mount. Reopening/reloading
   the collection mounts a new preview; live editing uses editor-frame instead."
  [{:keys [snapshot compose-fn] :or {compose-fn render/compose!}}]
  (let [*state                 (r/atom {})
        *mounted?              (atom true)
        *candidate             (atom nil)
        controller             (js/AbortController.)
        {:keys [width height]} (:layout snapshot)]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (-> (js/Promise.resolve)
             (.then #(compose-fn (assoc snapshot :signal (.-signal controller))))
             (.then (fn [blob]
                      (when @*mounted?
                        (let [url   (js/URL.createObjectURL blob)
                              image (js/Image.)]
                          (reset! *candidate url)
                          (set! (.-src image) url)
                          (.decode image)))))
             (.then (fn []
                      (when @*mounted?
                        (reset! *state {:url @*candidate})
                        (reset! *candidate nil))))
             (.catch (fn [_]
                       (when @*mounted? (reset! *state {:error? true}))))
             (.finally #(when @*candidate (js/URL.revokeObjectURL @*candidate)))))
       :component-will-unmount
       (fn [_]
         (reset! *mounted? false)
         (.abort controller)
         (when @*candidate
           (js/URL.revokeObjectURL @*candidate)
           (reset! *candidate nil))
         (when-let [url (:url @*state)] (js/URL.revokeObjectURL url)))
       :reagent-render
       (fn [{:keys [tr]}]
         [:div {:style {:width "100%" :aspect-ratio (str width " / " height)}}
          (when (:error? @*state)
            [:p.absolute {:role "alert"} (tr [:stamp/render-error])])
          (if (:url @*state)
            [:img.block.w-full.h-full {:src (:url @*state) :alt (tr [:stamp/preview]) :draggable false}]
            (when-not (:error? @*state)
              [:p {:role "status"} (tr [:loading])]))])})))

(defn- editor-frame [_]
  (let [renderer    (render/editor-renderer)
        *state      (r/atom {})
        *node       (atom nil)
        *position   (atom nil)
        *pending    (atom nil)
        *latest     (atom nil)
        *controller (atom nil)
        *active?    (atom false)
        *mounted?   (atom true)
        *animation  (atom nil)
        scene       #(dissoc (:snapshot %) :viewport)
        schedule!
        (fn schedule! []
          (when (and @*mounted? @*pending (not @*active?) (nil? @*animation))
            (reset! *animation
                    (js/requestAnimationFrame
                      (fn []
                        (reset! *animation nil)
                        (let [{:keys [snapshot position] :as request} @*pending
                              controller                              (js/AbortController.)]
                          (reset! *pending nil)
                          (reset! *active? true)
                          (reset! *controller controller)
                          (-> ((:draw! renderer) (assoc snapshot :signal (.-signal controller)))
                              (.then (fn [frame]
                                       (when (and @*mounted? (= (scene request) (scene @*latest)))
                                         (let [node    @*node
                                               context (.getContext node "2d")]
                                           (set! (.-width node) (.-width frame))
                                           (set! (.-height node) (.-height frame))
                                           (.drawImage context frame 0 0)
                                           ;; Move the crop and its pixels in the same turn. Waiting for a
                                           ;; Reagent render here could briefly position new pixels at an old offset.
                                           (doseq [[key value] position]
                                             (aset (.. node -parentElement -style) (name key) (str value "px")))
                                           (reset! *position position)
                                           (reset! *state {:ready? true})))))
                              (.catch (fn [_]
                                        (when (and @*mounted? (= request @*latest))
                                          (swap! *state assoc :error? true))))
                              (.finally (fn []
                                          (reset! *active? false)
                                          (schedule!))))))))))
        refresh!
        (fn [component]
          (let [request (r/props component)]
            (when (and @*controller (not= (scene request) (scene @*latest)))
              (.abort @*controller))
            (reset! *latest request)
            (reset! *pending request)
            (schedule!)))]
    (r/create-class
      {:component-did-mount refresh!
       :component-did-update
       (fn [component old]
         (when (not= (select-keys (second old) [:snapshot :position])
                     (select-keys (r/props component) [:snapshot :position]))
           (refresh! component)))
       :component-will-unmount
       (fn [_]
         (reset! *mounted? false)
         (reset! *pending nil)
         (when @*animation (js/cancelAnimationFrame @*animation))
         (when @*controller (.abort @*controller))
         ((:dispose! renderer)))
       :reagent-render
       (fn [{:keys [tr position]}]
         [:div.absolute {:style (or @*position position)}
          [:canvas.block.w-full.h-full
           {:ref        #(reset! *node %)
            :role       "img"
            :aria-label (tr [:stamp/preview])}]
          (when (:error? @*state)
            [:p.absolute {:role "alert"} (tr [:stamp/render-error])])
          (when-not (or (:ready? @*state) (:error? @*state))
            [:p.absolute {:role "status"} (tr [:loading])])])})))

(defn- editor-preview [_]
  (let [*region     (r/atom nil)
        *node       (atom nil)
        *scroll     (atom nil)
        measure!
        (fn []
          (when (and @*node @*scroll)
            (let [rect (.getBoundingClientRect @*node)
                  port (.getBoundingClientRect @*scroll)
                  dpr  (.-devicePixelRatio js/window)
                  ;; Bounded overscan covers small scrolls while the next frame is prepared.
                  x    (max 0 (- (+ (.-left port) (.-clientLeft @*scroll)) (.-left rect)))
                  y    (max 0 (- (+ (.-top port) (.-clientTop @*scroll)) (.-top rect)))
                  left (max 0 (- x 96))
                  top  (max 0 (- y 96))
                  w    (min (- (.-width rect) left) (+ (- x left) (.-clientWidth @*scroll) 96))
                  h    (min (- (.-height rect) top) (+ (- y top) (.-clientHeight @*scroll) 96))]
              (when (and (pos? w) (pos? h))
                (reset! *region {:left   left
                                 :top    top
                                 :width  (/ (js/Math.ceil (* w dpr)) dpr)
                                 :height (/ (js/Math.ceil (* h dpr)) dpr)
                                 :dpr    dpr})))))
        observer    (js/ResizeObserver. measure!)
        *resolution (atom nil)
        watch-dpr!  (fn watch-dpr! []
                      (when @*resolution (.removeEventListener @*resolution "change" watch-dpr!))
                      (reset! *resolution
                              (.matchMedia js/window (str "(resolution: " (.-devicePixelRatio js/window) "dppx)")))
                      (.addEventListener @*resolution "change" watch-dpr!)
                      (measure!))]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (reset! *scroll (.closest @*node "[data-cy=stamp-canvas-scroll]"))
         (.observe observer @*node)
         (.observe observer @*scroll)
         (.addEventListener @*scroll "scroll" measure!)
         (.addEventListener js/window "resize" measure!)
         (watch-dpr!))
       :component-will-unmount
       (fn [_]
         (.disconnect observer)
         (.removeEventListener @*scroll "scroll" measure!)
         (.removeEventListener js/window "resize" measure!)
         (.removeEventListener @*resolution "change" watch-dpr!))
       :reagent-render
       (fn [{:keys [tr snapshot viewport scale]}]
         [:div.absolute.inset-0.overflow-hidden.pointer-events-none {:ref #(reset! *node %)}
          (when-let [{:keys [left top width height dpr]} @*region]
            [editor-frame {:tr       tr
                           :position {:left left :top top :width width :height height}
                           :snapshot (assoc snapshot :pixel-scale (* scale dpr)
                                            :viewport {:x      (+ (:x viewport) (/ left scale))
                                                       :y      (+ (:y viewport) (/ top scale))
                                                       :width  (/ width scale)
                                                       :height (/ height scale)})}])])})))

(defn editing-viewport
  "Symmetric margins keep the printable canvas centred and stored overflow recoverable.
   Use opening elements, not live dragged geometry, so the workspace does not chase a gesture."
  [{:keys [width height elements]}]
  (let [bounds   (map (fn [{:keys [x y width height]}]
                        (let [radius (/ (js/Math.hypot width height) 2)
                              cx     (+ x (/ width 2))
                              cy     (+ y (/ height 2))]
                          [(- cx radius) (- cy radius) (+ cx radius) (+ cy radius)])) elements)
        margin-x (max 144 (- 36 (reduce min 0 (map first bounds)))
                      (+ 36 (- (reduce max width (map #(nth % 2) bounds)) width)))
        margin-y (max 144 (- 36 (reduce min 0 (map second bounds)))
                      (+ 36 (- (reduce max height (map #(nth % 3) bounds)) height)))]
    {:x      (- margin-x)
     :y      (- margin-y)
     :width  (+ width (* 2 margin-x))
     :height (+ height (* 2 margin-y))}))

(defn element-bounds [{:keys [x y width height rotation]}]
  (let [angle (* rotation (/ js/Math.PI 180))
        c     (js/Math.abs (js/Math.cos angle))
        s     (js/Math.abs (js/Math.sin angle))
        w     (+ (* width c) (* height s))
        h     (+ (* width s) (* height c))]
    {:x      (- (+ x (/ width 2)) (/ w 2))
     :y      (- (+ y (/ height 2)) (/ h 2))
     :width  w
     :height h}))

(defn constrain-element [previous element viewport]
  (let [{:keys [x y width height]} (element-bounds element)]
    (if (or (> width (:width viewport)) (> height (:height viewport)))
      previous
      (-> element
          (update :x + (- (max (:x viewport) (min x (- (+ (:x viewport) (:width viewport)) width))) x))
          (update :y + (- (max (:y viewport) (min y (- (+ (:y viewport) (:height viewport)) height))) y))))))

(defn transform-element [element mode dx dy]
  (let [{:keys [x y width height rotation]} element
        radians                             (* rotation (/ js/Math.PI 180))
        local-x                             (+ (* dx (js/Math.cos radians)) (* dy (js/Math.sin radians)))
        local-y                             (- (* dy (js/Math.cos radians)) (* dx (js/Math.sin radians)))
        image?                              (= :image (:type element))]
    (case mode
      :rotate (assoc element :rotation (-> (+ rotation dx) (max -360) (min 360)))
      :resize
      (if (#{:text :date} (:type element))
        element
        (let [ratio   (/ width height)
              w       (-> (+ width local-x)
                          (max (if image? (* 0.72 (max 1 ratio)) 0.72))
                          (min (if image? (* stamp-model/max-coordinate (min 1 ratio)) stamp-model/max-coordinate)))
              h       (if image? (* height (/ w width))
                          (-> (+ height local-y) (max 0.72) (min stamp-model/max-coordinate)))
              dw      (- w width)
              dh      (- h height)
              shift-x (* 0.5 (- (* dw (js/Math.cos radians)) (* dh (js/Math.sin radians)) dw))
              shift-y (* 0.5 (- (+ (* dw (js/Math.sin radians)) (* dh (js/Math.cos radians))) dh))]
          ;; Hold the rotated top-left corner fixed while the opposite handle moves.
          (assoc element :width w :height h :x (+ x shift-x) :y (+ y shift-y))))
      (assoc element :x (+ x dx) :y (+ y dy)))))

(defn- element-handle [{:keys [tr element index viewport scale selected? disabled? on-select on-change]}]
  (r/with-let [*gesture (atom nil)]
    (let [{:keys [id x y width height rotation type]} element
          start!
          (fn [event mode]
            (.stopPropagation event)
            (when (and (not disabled?) (= 0 (.-button event)))
              (.preventDefault event)
              (on-select id)
              (let [target (.-currentTarget event)]
                (.focus target #js {:preventScroll true})
                (reset! *gesture {:element element
                                  :mode    mode
                                  :x       (.-clientX event)
                                  :y       (.-clientY event)})
                (.setPointerCapture target (.-pointerId event)))))
          move!
          (fn [event]
            (when-let [{:keys [element mode x y]} @*gesture]
              (on-change (constrain-element element
                                            (transform-element element mode (/ (- (.-clientX event) x) scale)
                                                               (/ (- (.-clientY event) y) scale)) viewport))))
          keyboard!
          (fn [event mode]
            (when-let [[dx dy] (get {"ArrowLeft" [-1 0] "ArrowRight" [1 0]
                                     "ArrowUp"   [0 -1] "ArrowDown"  [0 1]} (.-key event))]
              (.preventDefault event)
              (.stopPropagation event)
              (on-change (constrain-element element (transform-element element mode dx dy) viewport))))]
      [:div.absolute.border.border-dashed
       {:role                    "button"
        :tab-index               (if disabled? -1 0)
        :aria-label              (str (inc index) ". " (tr [(keyword "stamp" (name type))]))
        :aria-pressed            selected?
        :on-focus                #(on-select id)
        :class                   (if selected? "border-blue-600" "border-gray-400")
        :style                   {:left         (* scale (- x (:x viewport)))
                                  :top          (* scale (- y (:y viewport)))
                                  :width        (* scale width)
                                  :height       (* scale height)
                                  :transform    (str "rotate(" rotation "deg)")
                                  :cursor       "move"
                                  :touch-action "none"}
        :on-pointer-down         #(start! % :move)
        :on-pointer-move         move!
        :on-pointer-up           #(reset! *gesture nil)
        :on-lost-pointer-capture #(reset! *gesture nil)
        :on-pointer-cancel       #(reset! *gesture nil)
        :on-key-down             #(keyboard! % :move)}
       (when selected?
         (futil/for-frag [[mode label class] (cond-> [[:rotate :stamp/rotate "-top-6 right-0"]]
                                               (#{:image :signature} type)
                                               (conj [:resize :stamp/resize "bottom-0 right-0"]))]
           [:button.absolute.bg-white.border.border-blue-600.w-6.h-6
            {:type                    "button"
             :class                   class
             :aria-label              (tr [label])
             :disabled                disabled?
             :style                   {:touch-action "none"}
             :on-pointer-down         #(start! % mode)
             :on-pointer-move         move!
             :on-pointer-up           #(reset! *gesture nil)
             :on-lost-pointer-capture #(reset! *gesture nil)
             :on-pointer-cancel       #(reset! *gesture nil)
             :on-key-down             #(keyboard! % mode)}
            [:img.block.w-4.h-4.mx-auto
             {:src (icons/icon->img-svg-src (if (= mode :resize) icons/expand-down-right icons/rotate-right))
              :alt ""}]]))])))

(defn display-scale [layout available-width]
  ;; Fit the printable area, leaving 48 CSS pixels of useful overflow on each side.
  (min (/ (max 72 (- available-width 96)) (:width layout)) (/ 384 (:height layout))))

(defn stamp-canvas
  "Controlled canvas. `snapshot` contains layout, date/name context and image URLs.
   `viewport` is computed from opening geometry using editing-viewport; the caller
   can expand it after text/property edits, but must keep it stable during gestures.
   `on-change` receives the edited element. `toolbar` is optional caller-owned hiccup."
  [{:keys [tr snapshot viewport selected-id disabled? on-select on-change toolbar]}]
  (r/with-let [*available-width (r/atom 600)
               *locked-scroll   (atom nil)
               observer         (js/ResizeObserver.
                                  (fn [entries]
                                    (let [node   (.-target (aget entries 0))
                                          scroll (if (= "stamp-canvas-scroll" (.getAttribute node "data-cy"))
                                                   node (.. node -parentElement -parentElement))]
                                      (reset! *available-width (.-clientWidth scroll))
                                      (set! (.-scrollLeft scroll) (/ (- (.-scrollWidth scroll) (.-clientWidth scroll)) 2))
                                      (set! (.-scrollTop scroll) (/ (- (.-scrollHeight scroll) (.-clientHeight scroll)) 2)))))
               observe!         (fn [node]
                                  (when node
                                    (.observe observer node)
                                    (.observe observer (.. node -parentElement -parentElement))))]
    (let [layout (render/measured-layout (:layout snapshot))
          scale  (display-scale layout @*available-width)]
      [:div.min-w-0.overflow-hidden.rounded.border.border-solid.border-gray-300.bg-gray-100
       {:data-cy    "stamp-editor-surface"
        :role       "region"
        :aria-label (tr [:stamp/canvas])
        :class      "flex-[999_1_36rem]"}
       (when toolbar [:div.flex.justify-center.p-3 toolbar])
       [:div.overflow-auto
        {:data-cy                 "stamp-canvas-scroll"
         :style                   {:max-height "560px" :overflow-anchor "none"}
         :on-pointer-down-capture #(let [node (.-currentTarget %)]
                                     (reset! *locked-scroll [(.-scrollLeft node) (.-scrollTop node)]))
         :on-scroll               #(when-let [[left top] @*locked-scroll]
                                     (let [node (.-currentTarget %)]
                                       (set! (.-scrollLeft node) left)
                                       (set! (.-scrollTop node) top)))
         :on-pointer-up           #(js/requestAnimationFrame (fn [] (reset! *locked-scroll nil)))
         :on-lost-pointer-capture #(js/requestAnimationFrame (fn [] (reset! *locked-scroll nil)))
         :on-pointer-cancel       #(reset! *locked-scroll nil)}
        [:div.grid.place-items-center.min-w-full
         {:style    {:width "max-content" :min-height "480px"}
          :on-click #(when (= (.-target %) (.-currentTarget %)) (on-select nil))}
         [:div.relative {:data-cy "stamp-workspace"
                         :ref     observe!
                         :style   {:width  (* scale (:width viewport))
                                   :height (* scale (:height viewport))}}
          [:p.absolute.text-sm.font-normal.text-gray-700.pb-2.pointer-events-none
           {:style {:left      (* (- scale) (:x viewport))
                    :top       (* (- scale) (:y viewport))
                    :transform "translateY(-100%)"}}
           (tr [:stamp/area])]
          [:div.absolute.bg-white.border.border-solid.border-gray-500.shadow-sm
           {:data-cy  "stamp-printable-area"
            :style    {:left   (* (- scale) (:x viewport))
                       :top    (* (- scale) (:y viewport))
                       :width  (* scale (:width layout))
                       :height (* scale (:height layout))}
            :on-click #(on-select nil)}]
          [editor-preview {:tr tr :snapshot (assoc snapshot :layout layout) :viewport viewport :scale scale}]
          (futil/for-frag [[index element] (map-indexed vector (:elements layout))]
            ^{:key (:id element)}
            [element-handle {:tr        tr
                             :index     index
                             :element   element
                             :viewport  viewport
                             :scale     scale
                             :selected? (= selected-id (:id element))
                             :disabled? disabled?
                             :on-select on-select
                             :on-change on-change}])]]]])
    (finally (.disconnect observer))))
